// CARO. Casa de estilo — SEO helpers (framework-agnostic, pure).
// Produces title/description/OG data and JSON-LD structured data. Used by the
// client to set document head, and by the Worker for sitemap generation.
//
// Also the single home of the URL-locale helpers: ES (DEFAULT_LOCALE) lives at
// the root, every other locale under `/<locale>/...` with translated segments
// (`/fr/boutique/maison`, table in ./routes). Code works on "logical" paths (the
// Spanish ones); localizedPath / stripLocalePrefix convert to and from the
// public URL. The client router, the Worker's <head>/sitemap and the error page
// all derive the locale from these.

import {
  BRAND,
  BUSINESS,
  CARO_PERSON,
  LOCALES,
  DEFAULT_LOCALE,
  EU_COUNTRIES,
  GOOGLE_BUSINESS_PROFILE_URL,
  OPENING_HOURS,
  SERVICE_AREA,
} from '@/config/site';
import { SERVICE_GROUPS, servicePath } from '@/content/services';
import type { Article, Locale, Product, PublicPack } from '@/types';
import { centsToDecimalString } from '@/lib/money';
import {
  applyFuelSurcharge,
  EUROZONE_COUNTRIES,
  FREE_SHIPPING_THRESHOLD_CENTS,
  hasDeliveryEstimate,
  SHIPPING_BUSINESS_DAYS,
  SHIPPING_TIMES,
  SHIPPING_ZONES,
  transitDays,
  type DayRange,
  type ShippingZone,
} from '@/lib/shipping';
import { pieceDeliveryCents } from '@/lib/cart';
import { isSoldOut } from '@/lib/catalog/availability';
import { offerItemCondition } from '@/lib/catalog/finds';
import { PACK_PATH, packName } from '@/lib/catalog/packs';
import { SITE_DESCRIPTION, STATIC_PAGES } from '@/lib/seo/pages';
import { toLogicalPath, toPublicPath } from '@/lib/seo/routes';

// Per-page title/description text (static pages, Edit categories, products).
export * from '@/lib/seo/pages';
// Translated URL segments per locale (toPublicPath, toLogicalPath, logicalRoutePattern).
export * from '@/lib/seo/routes';

// --- URL locale --------------------------------------------------------------

/**
 * Matches a non-default locale prefix as a WHOLE segment: `/fr` and `/fr/...`
 * match, `/frx` or `/french` don't. Built from LOCALES so a new locale needs no
 * regex change.
 */
const LOCALE_PREFIX_RE = new RegExp(
  `^/(${LOCALES.filter((l) => l !== DEFAULT_LOCALE).join('|')})(?=/|$)`,
);

/** Locale of a URL path (`/fr/shop` → fr); the default locale when unprefixed. */
export function localeFromPath(pathname: string): Locale {
  const match = LOCALE_PREFIX_RE.exec(pathname);
  return match ? (match[1] as Locale) : DEFAULT_LOCALE;
}

/**
 * A URL path without its locale prefix, still in that locale's (public)
 * segments: `/fr/boutique/maison` → `/boutique/maison`, `/fr` → `/`.
 */
export function unprefixedPath(pathname: string): string {
  const stripped = pathname.replace(LOCALE_PREFIX_RE, '');
  return stripped === '' ? '/' : stripped;
}

/**
 * Logical path of a public URL path: strip the locale prefix and untranslate
 * its segments (`/fr/boutique/maison/x` → `/shop/casa/x`). Old URLs with
 * Spanish segments (`/fr/contacto`) map to their logical path too.
 */
export function stripLocalePrefix(pathname: string): string {
  return toLogicalPath(localeFromPath(pathname), unprefixedPath(pathname));
}

/**
 * One of our locales from any stored/requested locale string (`orders.locale`,
 * `newsletter_subscribers.locale`, …): `fr`, `fr-FR` → fr; unknown or empty → es.
 */
export function toLocale(raw: string | null | undefined): Locale {
  const l = (raw ?? '').trim().toLowerCase();
  return LOCALES.find((loc) => l === loc || l.startsWith(`${loc}-`) || l.startsWith(`${loc}_`)) ?? DEFAULT_LOCALE;
}

/**
 * Public URL path of a logical path in `locale`: translated segments behind the
 * locale prefix (`/shop/casa` → `/fr/boutique/maison`; unchanged for es).
 */
export function localizedPath(locale: Locale, path = '/'): string {
  const clean = toPublicPath(locale, path.startsWith('/') ? path : `/${path}`);
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === '/' ? `/${locale}` : `/${locale}${clean}`;
}

/** Open Graph `og:locale` value per locale. */
export const OG_LOCALE: Record<Locale, string> = {
  es: 'es_ES',
  en: 'en_GB',
  fr: 'fr_FR',
};

/** BCP 47 tag for `Intl` formatting (dates, region names) per locale. */
export const INTL_LOCALE: Record<Locale, string> = {
  es: 'es-ES',
  en: 'en-GB',
  fr: 'fr-FR',
};

/** Brand tagline per locale (home page <title>). */
const TAGLINE: Record<Locale, string> = {
  es: BRAND.tagline,
  en: 'Style for the woman who evolves.',
  fr: 'Le style pour la femme qui évolue.',
};

/** Make a `/media/...`-style path absolute against the production origin. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = BRAND.productionUrl.replace(/\/$/, '');
  return `${base}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

const TITLE_SUFFIX = BRAND.name;

export interface MetaInput {
  title?: string;
  /** og:title (and the og:image alt) when it differs from `title` (product pages: the name). */
  ogTitle?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  /** Logical path per language when it is not `path` translated (La carta: one slug per language). */
  localePaths?: Partial<Record<Locale, string>>;
}

export interface ResolvedMeta {
  title: string;
  ogTitle: string;
  description: string;
  canonical: string;
  ogImage: string | undefined;
  type: string;
  noindex: boolean;
}

/** Full <title>: `<title> · CARO. Casa de estilo`, or brand + local tagline for the home. */
export function buildTitle(title?: string, locale: Locale = DEFAULT_LOCALE): string {
  if (!title) return `${TITLE_SUFFIX} · ${TAGLINE[locale]}`;
  return `${title} · ${TITLE_SUFFIX}`;
}

export function canonicalUrl(path = '/'): string {
  const base = BRAND.productionUrl.replace(/\/$/, '');
  const clean = path.startsWith('/') ? path : `/${path}`;
  // Strip tracking params so ?utm_source=instagram never breaks canonicals.
  const [pathname] = clean.split('?');
  return `${base}${pathname === '/' ? '' : pathname}`;
}

/** Resolve page meta for `locale`; `canonical` is the localized absolute URL. */
export function resolveMeta(input: MetaInput, locale: Locale = DEFAULT_LOCALE): ResolvedMeta {
  return {
    title: buildTitle(input.title, locale),
    ogTitle: buildTitle(input.ogTitle ?? input.title, locale),
    description: input.description ?? SITE_DESCRIPTION[locale],
    canonical: canonicalUrl(localizedPath(locale, input.path ?? '/')),
    ogImage: input.image,
    type: input.type ?? 'website',
    noindex: input.noindex ?? false,
  };
}

// --- JSON-LD ---------------------------------------------------------------

/** Stable node id of the organization: other nodes reference it via organizationRef(). */
const ORGANIZATION_ID = `${BRAND.productionUrl.replace(/\/$/, '')}/#organization`;

/** Stable node id of the organization's shipping policy (hasShippingService). */
const SHIPPING_SERVICE_ID = `${BRAND.productionUrl.replace(/\/$/, '')}/#shipping`;

/** Stable node id of the organization's returns policy (hasMerchantReturnPolicy). */
const RETURN_POLICY_ID = `${BRAND.productionUrl.replace(/\/$/, '')}/#returns`;

/** Brand icon (1024×1024 PNG, public/brand/): search engines want a raster logo. */
const ORGANIZATION_LOGO = `${BRAND.productionUrl.replace(/\/$/, '')}/brand/caro-evolucion-icono-negro.png`;

/** Short reference to the organization node (`@id` of organizationJsonLd()). */
export function organizationRef(): { '@id': string } {
  return { '@id': ORGANIZATION_ID };
}

// --- Commerce policies. Property names checked against Google Search Central
// via Context7 on 2026-10-13 (/websites/developers_google_search_appearance_structured-data:
// "return-policy", "shipping-policy" and "merchant-listing"). Every number is
// derived from code (SHIPPING_ZONES / D1 zones, applyFuelSurcharge,
// SHIPPING_TIMES, legal §22/§25), never written by hand here.

/** Withdrawal period of legal §22 (14 natural days from receipt). */
export const RETURN_WINDOW_DAYS = 14;

/** EEA members outside the EU (the EU list is EU_COUNTRIES in config/site). */
const EEA_NON_EU = new Set(['IS', 'LI', 'NO']);

/** Public page with the returns terms (§22-§34 of the purchase conditions). */
const RETURN_POLICY_PATH = '/legal/condiciones-compra';

/** Money for a schema.org MonetaryAmount `value` (euros as a number, from integer cents). */
const amount = (cents: number) => Number(centsToDecimalString(cents));

/** schema.org QuantitativeValue of whole days. */
const days = (r: DayRange) => ({ '@type': 'QuantitativeValue', minValue: r.min, maxValue: r.max, unitCode: 'DAY' });

/** ServicePeriod (org-level handlingTime / transitTime): business days Monday-Friday. */
const servicePeriod = (r: DayRange) => ({
  '@type': 'ServicePeriod',
  duration: days(r),
  businessDays: SHIPPING_BUSINESS_DAYS.map((d) => `https://schema.org/${d}`),
});

/**
 * DefinedRegion(s) of a zone. A catch-all zone (`*`) gets NO destination: per
 * Google, conditions without shippingDestination apply worldwide, and when
 * several conditions match a country the LOWEST rate is used, so the specific
 * (cheaper) zones still win for their own countries.
 */
function zoneDestination(zone: ShippingZone): Record<string, unknown> {
  const countries = zone.countries.filter((c) => c !== '*');
  if (zone.countries.includes('*') || countries.length === 0) return {};
  return { shippingDestination: countries.map((c) => ({ '@type': 'DefinedRegion', addressCountry: c })) };
}

/** Enabled zones in config order (`zones` = what the checkout reads: D1, else the built-in config). */
const enabledZones = (zones: ShippingZone[]) => zones.filter((z) => z.enabled);

/**
 * Countries the returns policy covers: the served EU/EEA countries (España +
 * Europa zones), where the legal withdrawal right of §22 applies. Max 50 (Google).
 */
export function returnPolicyCountries(zones: ShippingZone[] = SHIPPING_ZONES): string[] {
  const served = enabledZones(zones).flatMap((z) => z.countries);
  return [...new Set(served)].filter((c) => EU_COUNTRIES.has(c) || EEA_NON_EU.has(c)).sort().slice(0, 50);
}

/**
 * MerchantReturnPolicy (Organization.hasMerchantReturnPolicy): 14 days (§22),
 * by mail, return shipping paid by the customer (§25 →
 * ReturnFeesCustomerResponsibility, which Google says must NOT carry a
 * return-shipping amount), full refund. The exceptions (unsealed cosmetics,
 * §34) stay in the legal text.
 */
export function merchantReturnPolicyJsonLd(zones: ShippingZone[] = SHIPPING_ZONES): Record<string, unknown> {
  return {
    '@type': 'MerchantReturnPolicy',
    '@id': RETURN_POLICY_ID,
    applicableCountry: returnPolicyCountries(zones),
    returnPolicyCountry: 'ES',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: RETURN_WINDOW_DAYS,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
    refundType: 'https://schema.org/FullRefund',
    merchantReturnLink: canonicalUrl(RETURN_POLICY_PATH),
  };
}

/**
 * ShippingService (Organization.hasShippingService) for standard parcels: one
 * ShippingConditions per enabled zone with the rate the checkout charges (zone
 * rate + fuel surcharge, applyFuelSurcharge as in computeOrderTotals), plus 0 €
 * from FREE_SHIPPING_THRESHOLD_CENTS for the eurozone countries of each zone
 * (calculateShipping's free rule). That free rule only counts standard items:
 * large pieces carry their own per-piece delivery and override this policy with
 * offers.shippingDetails (productJsonLd; Google: product-level markup beats
 * organization-level), so this policy describes exactly the parcels it governs.
 * Timings from SHIPPING_TIMES.
 */
export function shippingServiceJsonLd(zones: ShippingZone[] = SHIPPING_ZONES): Record<string, unknown> {
  const conditions: Record<string, unknown>[] = [];
  for (const zone of enabledZones(zones)) {
    const transit = transitDays(zone.id);
    const timing = transit ? { transitTime: servicePeriod(transit) } : {};
    conditions.push({
      '@type': 'ShippingConditions',
      ...zoneDestination(zone),
      shippingRate: { '@type': 'MonetaryAmount', value: amount(applyFuelSurcharge(zone.rate.amountCents)), currency: 'EUR' },
      ...timing,
    });
    const euro = zone.countries.filter((c) => EUROZONE_COUNTRIES.has(c));
    if (euro.length > 0) {
      conditions.push({
        '@type': 'ShippingConditions',
        shippingDestination: euro.map((c) => ({ '@type': 'DefinedRegion', addressCountry: c })),
        orderValue: { '@type': 'MonetaryAmount', minValue: amount(FREE_SHIPPING_THRESHOLD_CENTS), currency: 'EUR' },
        shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'EUR' },
        ...timing,
      });
    }
  }
  return {
    '@type': 'ShippingService',
    '@id': SHIPPING_SERVICE_ID,
    fulfillmentType: 'https://schema.org/FulfillmentTypeDelivery',
    handlingTime: servicePeriod(SHIPPING_TIMES.handling),
    shippingConditions: conditions,
  };
}

/**
 * The organization as an OnlineStore (an Organization subtype) with its
 * returns and shipping policies. Full node on `/` and `/caro`; other nodes point
 * at it with organizationRef(). `zones`: the checkout's shipping zones (D1 via
 * listShippingZones, else the built-in SHIPPING_ZONES).
 */
export function organizationJsonLd(zones: ShippingZone[] = SHIPPING_ZONES): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    '@id': ORGANIZATION_ID,
    name: BRAND.name,
    url: BRAND.productionUrl,
    logo: ORGANIZATION_LOGO,
    description:
      'Casa de estilo, belleza y bienestar para la mujer que evoluciona. Selección personal de Carolina Collaer.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: BRAND.contactEmail,
      contactType: 'customer service',
      availableLanguage: [...LOCALES],
    },
    sameAs: [BRAND.instagramUrl],
    hasMerchantReturnPolicy: merchantReturnPolicyJsonLd(zones),
    hasShippingService: shippingServiceJsonLd(zones),
  };
}

/** The home page's WebSite node (next to the organization; only on `/`). */
export function websiteJsonLd(locale: Locale): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND.name,
    alternateName: ['CARO'],
    url: canonicalUrl(localizedPath(locale, '/')),
    inLanguage: locale,
    publisher: organizationRef(),
  };
}

/**
 * Carolina as a schema.org Person (CARO_PERSON), for `locale`: `url` is her
 * localized /caro profile page. No `@context` (embed it, or wrap it as the
 * ProfilePage's mainEntity).
 */
export function personJsonLd(locale: Locale): Record<string, unknown> {
  return {
    '@type': 'Person',
    name: CARO_PERSON.name,
    jobTitle: CARO_PERSON.jobTitle[locale],
    url: canonicalUrl(localizedPath(locale, CARO_PERSON.path)),
    image: absoluteUrl(CARO_PERSON.image),
    sameAs: [...CARO_PERSON.sameAs],
    worksFor: organizationRef(),
  };
}

/** /caro: a ProfilePage whose main entity is Carolina (Person). */
export function profilePageJsonLd(locale: Locale): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    url: canonicalUrl(localizedPath(locale, CARO_PERSON.path)),
    inLanguage: locale,
    mainEntity: personJsonLd(locale),
  };
}

/** Stable node id of the image-consulting business (ProfessionalService on /caro/servicios). */
const SERVICES_ID = `${BRAND.productionUrl.replace(/\/$/, '')}/#asesoria`;

/** Logical path of the services page, and the photo it shows (CaroPages ServicesPage). */
const SERVICES_PATH = '/caro/servicios';
const SERVICES_IMAGE = '/media/caro/asesoria-de-imagen-carolina-collaer-dior-paris.webp';

/**
 * /caro/servicios: Carolina's image consulting as a schema.org
 * ProfessionalService (a LocalBusiness subtype; properties checked against
 * Google Search Central "local-business" via Context7, plan SEO local fase 3).
 * Address without street nor `geo` (D1: no clients at home), `areaServed` from
 * SERVICE_AREA (D2), hours from OPENING_HOURS, one Offer per SERVICE_GROUPS service with
 * its `priceFrom` (none = by format / quote), `sameAs` + the Google profile once it exists (D4).
 * No `aggregateRating` / `review` (Google: not for self-reviews). `t` is the
 * locale's dictionary (shared code must not import the client one).
 */
export function professionalServiceJsonLd(locale: Locale, t: (key: string) => string): Record<string, unknown> {
  const services = SERVICE_GROUPS.flatMap((g) => g.services);
  const prices = services.flatMap((s) => (s.priceFrom ? [s.priceFrom] : []));
  // "Can Trabal, Olèrdola" → municipality "Olèrdola" (same split as the DHL CSV).
  const municipality = BUSINESS.address.locality.split(',').pop()?.trim() ?? BUSINESS.address.locality;
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': SERVICES_ID,
    name: BRAND.name,
    url: canonicalUrl(localizedPath(locale, SERVICES_PATH)),
    description: STATIC_PAGES[SERVICES_PATH]![locale].description,
    image: absoluteUrl(SERVICES_IMAGE),
    // The WhatsApp number (wa.me digits) with its + country prefix.
    telephone: `+${BRAND.whatsappUrl.replace(/\D/g, '')}`,
    email: BRAND.contactEmail,
    ...(prices.length ? { priceRange: `${Math.min(...prices)}-${Math.max(...prices)} €` } : {}),
    address: {
      '@type': 'PostalAddress',
      addressLocality: municipality,
      postalCode: BUSINESS.address.postalCode,
      addressRegion: BUSINESS.address.province,
      addressCountry: 'ES',
    },
    areaServed: SERVICE_AREA.flatMap((a) => a.places.map((p) => ({ '@type': p.type, name: p.name }))),
    // Regular hours only; "other times by appointment" is visible copy (common.hours).
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [...OPENING_HOURS.days],
        opens: OPENING_HOURS.opens,
        closes: OPENING_HOURS.closes,
      },
    ],
    founder: personJsonLd(locale),
    parentOrganization: organizationRef(),
    sameAs: [BRAND.instagramUrl, GOOGLE_BUSINESS_PROFILE_URL].filter(Boolean),
    knowsLanguage: [...LOCALES],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: t('shop.servicios.title'),
      itemListElement: services.map((s) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: t(`services.svc.${s.key}.name`), url: canonicalUrl(localizedPath(locale, servicePath(s.key))) },
        ...(s.priceFrom
          ? { priceSpecification: { '@type': 'PriceSpecification', minPrice: s.priceFrom, priceCurrency: 'EUR' } }
          : {}),
      })),
    },
  };
}

/**
 * A service page (/caro/servicios/<slug>, docs/plan-tests-y-servicios.md): schema.org Service offered by
 * the image-consulting business (the ProfessionalService node of /caro/servicios, by @id), in the same
 * area, with its starting price when it has one. `t` is the locale's dictionary.
 */
export function serviceJsonLd(locale: Locale, t: (key: string) => string, key: string): Record<string, unknown> {
  const svc = SERVICE_GROUPS.flatMap((g) => g.services).find((s) => s.key === key);
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t(`services.svc.${key}.name`),
    serviceType: t('shop.servicios.title'),
    description: t(`services.page.${key}.metaDescription`),
    url: canonicalUrl(localizedPath(locale, servicePath(key))),
    provider: { '@type': 'ProfessionalService', '@id': SERVICES_ID, name: BRAND.name, url: canonicalUrl(localizedPath(locale, SERVICES_PATH)) },
    areaServed: SERVICE_AREA.flatMap((a) => a.places.map((p) => ({ '@type': p.type, name: p.name }))),
    ...(svc?.priceFrom ? { offers: { '@type': 'Offer', priceSpecification: { '@type': 'PriceSpecification', minPrice: svc.priceFrom, priceCurrency: 'EUR' } } } : {}),
  };
}

/**
 * A free style test of the Estudio de estilo (colour, silhouette, face) as a WebApplication that runs in
 * the browser, published by CARO. `url` = the canonical page URL.
 */
export function webApplicationJsonLd(opts: { name: string; description: string; url: string; locale: Locale }): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: opts.name,
    description: opts.description,
    url: opts.url,
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    publisher: { '@type': 'Organization', ...organizationRef(), name: BRAND.name },
    inLanguage: opts.locale,
  };
}

/** GS1 check digit of an EAN-8 / UPC-A / EAN-13 / GTIN-14 (digits only). */
function gtinCheckDigitOk(code: string): boolean {
  const digits = code.split('').map(Number);
  const check = digits.pop()!;
  const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/** `metadata.ean` when it is a well-formed GTIN (8, 12, 13 or 14 digits with a valid check digit). */
export function validGtin(ean: unknown): string | undefined {
  if (typeof ean !== 'string') return undefined;
  const code = ean.trim();
  return /^\d{8}$|^\d{12,14}$/.test(code) && gtinCheckDigitOk(code) ? code : undefined;
}

/** Main image + gallery, absolute and de-duplicated, in display order (like the PDP). */
export function productImages(product: Pick<Product, 'image' | 'gallery'>): string[] {
  const urls = [product.image, ...product.gallery.map((g) => g.url)]
    .filter((u): u is string => !!u)
    .map((u) => absoluteUrl(u));
  return [...new Set(urls)];
}

/**
 * offers.shippingDetails of a piece with its own delivery (metadata
 * deliverySpainCents > 0: Casa): one OfferShippingDetails per enabled zone with
 * the per-unit amount the checkout charges (pieceDeliveryCents: zone override or
 * Spain base × tier multiplier, then applyFuelSurcharge, as computeOrderTotals).
 * deliveryTime from SHIPPING_TIMES, omitted for pre-orders (hasDeliveryEstimate).
 * None for enquiry products (quotation) nor for standard items (the
 * organization's hasShippingService covers those).
 */
export function productShippingDetails(
  product: Product,
  zones: ShippingZone[] = SHIPPING_ZONES,
): Record<string, unknown>[] | undefined {
  const m = product.metadata;
  if (product.checkoutMode === 'enquiry' || !((m.deliverySpainCents ?? 0) > 0)) return undefined;
  const timed = hasDeliveryEstimate(product);
  // Google requires shippingDestination on OfferShippingDetails, so the
  // catch-all international zone (`*`, no country list) is left out here.
  return enabledZones(zones).filter((zone) => !zone.countries.includes('*')).map((zone) => {
    const transit = transitDays(zone.id);
    return {
      '@type': 'OfferShippingDetails',
      ...zoneDestination(zone),
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: amount(applyFuelSurcharge(pieceDeliveryCents(m, zone.id))),
        currency: product.currency,
      },
      ...(timed && transit
        ? {
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: days(SHIPPING_TIMES.handling),
              transitTime: days(transit),
            },
          }
        : {}),
    };
  });
}

/**
 * Policy links of an Offer (Google merchant listings): the organization's
 * returns policy by `@id`, and the piece's own shippingDetails or, for standard
 * parcels, an OfferShippingDetails pointing at the organization's
 * ShippingService by `@id` (Google's documented way to reference a global
 * policy from an Offer; without it Search Console reports the fields as
 * missing). `shipping: false` (enquiry: quotation) → no shipping link.
 */
function offerPolicyRefs(
  shippingDetails: Record<string, unknown>[] | undefined,
  shipping = true,
): Record<string, unknown> {
  return {
    hasMerchantReturnPolicy: { '@id': RETURN_POLICY_ID },
    ...(shippingDetails
      ? { shippingDetails }
      : shipping
        ? { shippingDetails: { '@type': 'OfferShippingDetails', hasShippingService: { '@id': SHIPPING_SERVICE_ID } } }
        : {}),
  };
}

/** The latest of `dates` as an ISO 8601 instant for Offer.validFrom; undefined when none parses. */
function latestInstant(...dates: (string | undefined)[]): string | undefined {
  const times = dates.map((d) => (d ? Date.parse(d) : NaN)).filter((t) => !Number.isNaN(t));
  return times.length ? new Date(Math.max(...times)).toISOString() : undefined;
}

/**
 * Product structured data. Pass the product already localized for `locale`
 * (localizeProduct); `offers.url` is that locale's canonical page URL. `zones`:
 * the checkout's shipping zones (D1, else the built-in SHIPPING_ZONES), for the
 * shippingDetails of pieces with their own delivery.
 */
export function productJsonLd(
  product: Product,
  locale: Locale,
  zones: ShippingZone[] = SHIPPING_ZONES,
): Record<string, unknown> {
  // Rich-results want ABSOLUTE image URLs. Availability reflects the real
  // state: pre-order (reasortimiento) → PreOrder; inactive or sold out
  // (isSoldOut, as the PDP: every piece is stock-based, sized ones by their
  // total) → OutOfStock; otherwise InStock.
  const availability = !isActive(product)
    ? 'https://schema.org/OutOfStock'
    : product.metadata.preorder
      ? 'https://schema.org/PreOrder'
      : isSoldOut(product)
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock';
  const images = productImages(product);
  const gtin = validGtin(product.metadata.ean);
  const shippingDetails = productShippingDetails(product, zones);
  const validFrom = latestInstant(product.updatedAt);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
    ...(images.length ? { image: images } : {}),
    ...(product.description ? { description: product.description } : {}),
    ...(product.sku ? { sku: product.sku } : {}),
    ...(gtin ? { gtin } : {}),
    offers: {
      '@type': 'Offer',
      priceCurrency: product.currency,
      price: centsToDecimalString(product.priceCents),
      availability,
      // New stock, or a find's own condition (lib/catalog/finds offerItemCondition).
      itemCondition: offerItemCondition(product),
      // Google's Offer wants a validity window: from the last edit of the product
      // (the current price applies since then at the latest), rolling to the end of next year.
      ...(validFrom ? { validFrom } : {}),
      priceValidUntil: `${new Date().getUTCFullYear() + 1}-12-31`,
      url: canonicalUrl(localizedPath(locale, `/shop/${product.category}/${product.slug}`)),
      seller: organizationRef(),
      ...offerPolicyRefs(shippingDetails, product.checkoutMode !== 'enquiry'),
    },
  };
}

/**
 * A pack page's Product structured data (docs/plan-packs.md), from the
 * PublicPack as the API serves it. `name` = the pack name in `locale`
 * (packName), `description` = the page's meta description (packPageText),
 * `image` = the pack's own photo, else the components' main photos (absolute,
 * de-duplicated). `offers` only while the pack can be bought (`available`):
 * the server-computed pack price, PreOrder when a component is a pre-order
 * (reasortimiento), else InStock; `url` = the pack page in `locale`.
 */
export function packJsonLd(
  pack: Pick<
    PublicPack,
    | 'slug'
    | 'name'
    | 'i18n'
    | 'discountPercent'
    | 'primaryCategory'
    | 'seq'
    | 'image'
    | 'components'
    | 'priceCents'
    | 'available'
    | 'updatedAt'
  >,
  locale: Locale,
  description: string,
): Record<string, unknown> {
  const images = [
    ...new Set(
      (pack.image ? [pack.image] : pack.components.map((c) => c.image))
        .filter((u): u is string => !!u)
        .map((u) => absoluteUrl(u)),
    ),
  ];
  const preorder = pack.components.some((c) => c.metadata.preorder === true);
  const currency = pack.components[0]?.currency ?? 'EUR';
  // A component with its own delivery (Casa) or by quotation → the standard
  // shipping policy does not describe the pack: no shipping link.
  const standardShipping = pack.components.every(
    (c) => c.checkoutMode !== 'enquiry' && !((c.metadata.deliverySpainCents ?? 0) > 0),
  );
  // The pack price follows its own discount and every component's price.
  const validFrom = latestInstant(pack.updatedAt, ...pack.components.map((c) => c.updatedAt));
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: packName(pack, locale),
    ...(images.length ? { image: images } : {}),
    ...(description ? { description } : {}),
    ...(pack.available && pack.priceCents > 0
      ? {
          offers: {
            '@type': 'Offer',
            priceCurrency: currency,
            price: centsToDecimalString(pack.priceCents),
            availability: preorder ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            ...(validFrom ? { validFrom } : {}),
            priceValidUntil: `${new Date().getUTCFullYear() + 1}-12-31`,
            url: canonicalUrl(localizedPath(locale, `${PACK_PATH}/${pack.slug}`)),
            seller: organizationRef(),
            ...offerPolicyRefs(undefined, standardShipping),
          },
        }
      : {}),
  };
}

function isActive(product: Product): boolean {
  return product.status === 'active';
}

/**
 * Article/BlogPosting structured data for The Edit posts. `image` should already
 * be an absolute URL (pass `absoluteUrl(article.heroImage)`); `url` is the
 * canonical page URL; `locale` is the language of the (localized) article.
 */
export function articleJsonLd(
  article: Article,
  opts: { url: string; image?: string; locale: Locale },
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.seoDescription ?? article.excerpt,
    ...(opts.image ? { image: opts.image } : {}),
    datePublished: article.date,
    dateModified: article.date,
    // Carolina's byline → her Person + profile url; any other byline is named as written.
    author:
      article.author === CARO_PERSON.name
        ? {
            '@type': 'Person',
            name: CARO_PERSON.name,
            jobTitle: CARO_PERSON.jobTitle[opts.locale],
            url: canonicalUrl(localizedPath(opts.locale, CARO_PERSON.path)),
          }
        : { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      ...organizationRef(),
      name: BRAND.name,
      logo: { '@type': 'ImageObject', url: ORGANIZATION_LOGO },
    },
    mainEntityOfPage: opts.url,
    inLanguage: opts.locale,
  };
}

/**
 * A page written by Carolina and published by CARO as an Article: a letter of «La carta de CARO»
 * (docs/plan-cartas.md) or the Estudio's guide «Combinar colores». `url` = the canonical page URL;
 * `image` absolute when the page has one; `modifiedAt` when the page is edited after publishing.
 */
export function carolinaArticleJsonLd(opts: {
  title: string;
  description: string;
  publishedAt: string;
  modifiedAt?: string;
  url: string;
  image?: string;
  locale: Locale;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.title,
    description: opts.description,
    ...(opts.image ? { image: opts.image } : {}),
    datePublished: opts.publishedAt,
    ...(opts.modifiedAt ? { dateModified: opts.modifiedAt } : {}),
    author: {
      '@type': 'Person',
      name: CARO_PERSON.name,
      jobTitle: CARO_PERSON.jobTitle[opts.locale],
      url: canonicalUrl(localizedPath(opts.locale, CARO_PERSON.path)),
    },
    publisher: {
      '@type': 'Organization',
      ...organizationRef(),
      name: BRAND.name,
      logo: { '@type': 'ImageObject', url: ORGANIZATION_LOGO },
    },
    mainEntityOfPage: opts.url,
    inLanguage: opts.locale,
  };
}

/**
 * Serialize a JSON-LD object for safe embedding inside a
 * `<script type="application/ld+json">` tag. `JSON.stringify` does NOT escape
 * `<`, `>` or `&`, so a value containing `</script>` (or U+2028/U+2029) could
 * break out of the tag — escape those to their `\uXXXX` forms. Use this instead
 * of raw `JSON.stringify` in any `dangerouslySetInnerHTML`.
 */
export function jsonLdString(data: Record<string, unknown>): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(new RegExp(String.fromCharCode(0x2028), 'g'), '\\u2028')
    .replace(new RegExp(String.fromCharCode(0x2029), 'g'), '\\u2029');
}

/** One entry of an ItemList: an absolute, localized page URL and its (localized) name. */
export interface ItemListEntry {
  url: string;
  name: string;
}

/**
 * schema.org ItemList (no `@context`: it is a CollectionPage's mainEntity).
 * `numberOfItems` = the entries listed, in order (`position` from 1).
 */
export function itemListJsonLd(items: ItemListEntry[]): Record<string, unknown> {
  return {
    '@type': 'ItemList',
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: item.url,
      name: item.name,
    })),
  };
}

/**
 * A listing page (shop category, the Edit and its categories) as a
 * CollectionPage whose mainEntity is the ItemList of what it lists. `url` is the
 * page's canonical (localized, absolute) URL.
 */
export function collectionPageJsonLd(opts: {
  url: string;
  name: string;
  description: string;
  locale: Locale;
  items: ItemListEntry[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    url: opts.url,
    name: opts.name,
    description: opts.description,
    inLanguage: opts.locale,
    mainEntity: itemListJsonLd(opts.items),
  };
}

export function breadcrumbJsonLd(
  items: { name: string; path: string }[],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}
